import {
    IMultiYearComparisonResponseDTO,
    IMultiYearNodeDTO,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ControlsState, DeltaMode } from '../models/controls.model';
import {
    classifyDelta,
    findNodeById,
    formatChfSigned,
    formatCurrency,
    formatPercentageSigned,
    formatStructPct,
    NO_DATA_PLACEHOLDER,
    ZERO_TOLERANCE,
} from '../models/multi-year-format.util';
import {
    AccountRowVM,
    DeltaCell,
    GapRowVM,
    RowVM,
    TreeRowVM,
    ValueCell,
} from '../models/row-vm.model';
import { hasNoNonZeroDescendantInYears, isAllZeroAcrossYears } from '../models/tree-walk.util';

interface BuildContext {
    /** Year indices (into `harmonized.yearLabels`) for the selected years, newest → oldest. */
    selectedYearIndices: number[];
    /** Index in `harmonized.yearLabels` of the newest selected year (drives Δ + colour). */
    newestIdx: number;
    /** Index in `harmonized.yearLabels` of the oldest selected year. */
    oldestIdx: number;
    structMode: boolean;
    deltaMode: DeltaMode;
    expandedNodes: Set<string>;
    referenceNodeId: string | null;
    /** Reference value per selected year — same indexing as `selectedYearIndices`. */
    referenceValues: number[];
}

export interface RowBuildResult {
    rows: RowVM[];
    /** Display labels for the year columns in render order (newest left → oldest right). */
    yearColumnLabels: string[];
}

export function buildRows(
    harmonized: IMultiYearComparisonResponseDTO,
    controls: ControlsState,
): RowBuildResult {
    const ctx = buildContext(harmonized, controls);
    if (ctx == null) {
        return { rows: [], yearColumnLabels: [] };
    }

    const rows: RowVM[] = [];

    if (controls.reportScope === 'bilanz') {
        // BS uses a per-section Verhältniskennzahl base — every line in
        // Aktiven measured against Total Aktiven (assets section root),
        // every line in Passiven against Total Passiven. Override the
        // ER-derived `referenceValues` per section before recursing.
        if (harmonized.assets) {
            const assetsCtx = withSectionReference(ctx, harmonized.assets);
            appendSection(harmonized.assets, assetsCtx, rows, /*isIncomeStatement*/ false);
        }
        if (harmonized.liabilities) {
            if (rows.length > 0) rows.push({ kind: 'gap' } as GapRowVM);
            const liabilitiesCtx = withSectionReference(ctx, harmonized.liabilities);
            appendSection(harmonized.liabilities, liabilitiesCtx, rows, false);
        }
    } else if (controls.reportScope === 'erfolg') {
        if (harmonized.incomeStatement) {
            // ER keeps the BE-resolved `referenceNodeId` (Betriebsertrag /
            // Nettoumsatz) as the 100% base — that's the standard ER convention.
            appendSection(harmonized.incomeStatement, ctx, rows, /*isIncomeStatement*/ true);
        }
    }

    const yearColumnLabels = ctx.selectedYearIndices.map((i) => harmonized.yearLabels[i]);
    return { rows, yearColumnLabels };
}

// ─── Internal ──────────────────────────────────────────────────────────────

function buildContext(
    harmonized: IMultiYearComparisonResponseDTO,
    controls: ControlsState,
): BuildContext | null {
    if (!harmonized || harmonized.yearLabels.length === 0) return null;

    const selectedSet = new Set(controls.selectedYears);
    const selectedYearIndices: number[] = [];
    harmonized.yearLabels.forEach((y, idx) => {
        if (selectedSet.has(y)) selectedYearIndices.push(idx);
    });
    if (selectedYearIndices.length === 0) return null;

    const newestIdx = selectedYearIndices[0];
    const oldestIdx = selectedYearIndices[selectedYearIndices.length - 1];
    const referenceNode = harmonized.referenceNodeId
        ? findNodeById(harmonized.incomeStatement, harmonized.referenceNodeId)
        : null;
    const referenceValues = selectedYearIndices.map((i) => referenceNode?.values[i] ?? 0);

    return {
        selectedYearIndices,
        newestIdx,
        oldestIdx,
        structMode: controls.structMode,
        deltaMode: controls.deltaMode,
        expandedNodes: new Set(controls.expandedNodes),
        referenceNodeId: harmonized.referenceNodeId,
        referenceValues,
    };
}

/**
 * Append a top-level section (assets / liabilities / income-statement).
 *
 * BS sections (assets, liabilities) keep the level-0 group at the **top** as a
 * teal section header — accountants read top-down from "Aktiven" to its breakdown.
 *
 * The IS section flips the order: children render first (Betriebsertrag, Aufwand,
 * subtotals), and the level-0 root is appended **at the bottom** as a prominent
 * "Total" row — matching how a printed Erfolgsrechnung reads from revenue down to
 * the final result line. Marked with a flag so the SCSS can give it the bold
 * `er-total` treatment.
 */
function appendSection(
    sectionRoot: IMultiYearNodeDTO,
    ctx: BuildContext,
    out: RowVM[],
    isIncomeStatement: boolean,
): void {
    if (hasNoNonZeroDescendant(sectionRoot, ctx)) return;

    if (!isIncomeStatement) {
        appendNodeRows(sectionRoot, /*level*/ 0, ctx, out);
        return;
    }

    // ER: render children first, then the section root as a "Total" row at the bottom.
    for (const child of sectionRoot.children) {
        appendNodeRows(child, 1, ctx, out);
    }
    out.push({
        kind: 'tree',
        nodeId: sectionRoot.id,
        level: 0,
        label: sectionRoot.label,
        nodeType: 'GROUP',
        isExpandable: false,
        isExpanded: true,
        values: buildValueCells(sectionRoot, ctx),
        delta: buildDeltaCell(sectionRoot, ctx),
        isReference: false,
        // Marker consumed by the table component → applies the `.er-total-row` class.
        isErTotal: true,
    });
}

/** Recursively append a node + (when expanded) its descendants. */
function appendNodeRows(
    node: IMultiYearNodeDTO,
    level: number,
    ctx: BuildContext,
    out: RowVM[],
): void {
    if (level > 0 && isAllZeroAcrossSelectedYears(node, ctx)) {
        // Hide leaves and inner nodes that are zero in every selected year (PRD §3.5).
        if (node.children.length === 0) return;
        if (node.children.every((c) => hasNoNonZeroDescendant(c, ctx))) return;
    }

    if (node.type === 'ACCOUNT') {
        out.push(buildAccountRow(node, level, ctx));
        return;
    }

    const isExpandable = node.children.length > 0 && node.type !== 'SUB_TOTAL';
    // Section roots (level 0) are always rendered as expanded — no toggle.
    const isExpanded = level === 0 || (isExpandable && ctx.expandedNodes.has(node.id));

    out.push({
        kind: 'tree',
        nodeId: node.id,
        level,
        label: node.label,
        nodeType: node.type as TreeRowVM['nodeType'],
        isExpandable: level > 0 && isExpandable,
        isExpanded,
        values: buildValueCells(node, ctx),
        delta: buildDeltaCell(node, ctx),
        isReference: node.isReferenceValue,
    });

    if (!isExpanded) return;

    for (const child of node.children) {
        appendNodeRows(child, level + 1, ctx, out);
    }
}

function buildAccountRow(node: IMultiYearNodeDTO, level: number, ctx: BuildContext): AccountRowVM {
    return {
        kind: 'account',
        nodeId: node.id,
        level,
        accountNumber: node.accountNumber ?? '',
        label: node.label,
        values: buildValueCells(node, ctx),
        delta: buildDeltaCell(node, ctx),
    };
}

function buildValueCells(node: IMultiYearNodeDTO, ctx: BuildContext): ValueCell[] {
    return ctx.selectedYearIndices.map((yearIdx, columnIdx) => {
        const raw = node.values[yearIdx] ?? 0;
        return {
            display: formatCurrency(raw),
            structPct: ctx.structMode
                ? formatStructPct(raw, ctx.referenceValues[columnIdx], node.isReferenceValue)
                : null,
            columnIndex: columnIdx,
        };
    });
}

/**
 * Build the Δ cell. Sign of the displayed value reflects whether the line item
 * **grew or shrank in magnitude** (not the raw arithmetic difference):
 *
 *   delta = (|newest| - |oldest|) / |oldest|
 *
 * Why magnitude — for a P&L Aufwand row going `-300 → -400`, the cost rose by
 * 33%; the user wants `+33.3 %` displayed (line item grew), even though the raw
 * arithmetic difference is `-100`. Same for revenue `1000 → 1200` → `+20.0 %`.
 *
 * Colour mapping:
 *   - Bilanz row → always neutral (grey badge).
 *   - ER Ertrag row + grew → green; shrank → red.
 *   - ER Aufwand row + grew → red; shrank → green.
 */
function buildDeltaCell(node: IMultiYearNodeDTO, ctx: BuildContext): DeltaCell {
    const newest = node.values[ctx.newestIdx] ?? 0;
    const oldest = node.values[ctx.oldestIdx] ?? 0;

    if (Math.abs(oldest) < ZERO_TOLERANCE) {
        return { display: NO_DATA_PLACEHOLDER, classification: 'na' };
    }

    const magnitudeDeltaChf = Math.abs(newest) - Math.abs(oldest);
    const magnitudeDeltaPct = (magnitudeDeltaChf / Math.abs(oldest)) * 100;
    const classification = classifyDelta(magnitudeDeltaChf, node);

    if (ctx.deltaMode === 'pct') {
        return { display: formatPercentageSigned(magnitudeDeltaPct), classification };
    }
    return { display: formatChfSigned(magnitudeDeltaChf), classification };
}

function isAllZeroAcrossSelectedYears(node: IMultiYearNodeDTO, ctx: BuildContext): boolean {
    return isAllZeroAcrossYears(node, ctx.selectedYearIndices);
}

function hasNoNonZeroDescendant(node: IMultiYearNodeDTO, ctx: BuildContext): boolean {
    return hasNoNonZeroDescendantInYears(node, ctx.selectedYearIndices);
}

/**
 * Derive a section-scoped context whose `referenceValues` come from the
 * given section root (per-year totals). Used so each BS section's
 * Verhältniskennzahl is computed against its own grand total instead of
 * against the global ER reference (Betriebsertrag).
 */
function withSectionReference(ctx: BuildContext, sectionRoot: IMultiYearNodeDTO): BuildContext {
    const referenceValues = ctx.selectedYearIndices.map((i) => sectionRoot.values[i] ?? 0);
    return { ...ctx, referenceValues };
}
